import {
  buildProductNatureOptionsFromProductNatureIds,
  buildRetailFamilyOptionsFromRetailFamilyIds,
  buildSportsOptionsFromSportsIds,
  SelectOptions,
  storageHelper,
  User,
} from '@spo/logic';
import { apiUsers } from '@spo/services/api';
import { Header, InputCalendar, Stepper } from '@spo/storybook';
import { format, isBefore } from 'date-fns';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { environment } from '../../environments/environment';
import { useUserConfig } from '../../hooks/useUserConfig';
import { todayDateAdd20years, tomorrowDate } from '../../utils/date';

import { SpoUserSettingsResponse } from '@spo/types';
import { VpDivider } from '@vtmn-play/react';
import { useRoles } from '../../contexts/RolesContext';
import { ErrorPage } from '../errorPage';
import { Layout } from '../layout';
import { ProductNaturesSearchInput } from '../select/productNature';
import { RetailFamiliesSearchInput } from '../select/retailFamily/RetailFamiliesSearchInput';
import { SportsSearchInput } from '../select/sport';
import { Welcome } from '../welcome';
import './myConfig.scss';

export const MyConfig = () => {
  const currentUser = storageHelper.getItem<User>('user')!;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const { hasRoleVisualization } = useRoles();

  const [sportsOptions, setSportsOptions] = useState<SelectOptions[]>([]);
  const [productNaturesOptions, setProductNaturesOptions] = useState<
    SelectOptions[]
  >([]);
  const [retailFamiliesOptions, setRetailFamiliesOptions] = useState<
    SelectOptions[]
  >([]);
  const [dateSelected, setDateSelected] = useState<Date[]>([]);
  const { data: userConfiguration } = useUserConfig();
  const [configBuilded, setConfigBuilded] = useState<boolean>(false);

  const sportsIds = sportsOptions.map((sport) => sport.value);
  const doesUserHaveSports = () => sportsIds.length > 0;
  const didUserSelectedADate = dateSelected.length > 0;
  const retailFamiliesDate = didUserSelectedADate
    ? format(dateSelected[0], 'yyyy-MM-dd')
    : '';
  const isDateInThePast = isBefore(dateSelected[0], new Date());
  const canSave = () => didUserSelectedADate && !isDateInThePast;
  const userHasConfig: boolean =
    !!userConfiguration && !userConfiguration.needRedirect;
  const shouldLoadStepper: boolean = !!currentUser && configBuilded;

  const buildConfiguration = async (config: SpoUserSettingsResponse) => {
    if (config.needRedirect) {
      setConfigBuilded(true);
      // because we don't have to build an empty configuration we just exit from the function
      return;
    }
    if (config.productNatureIds && config.productNatureIds.length > 0) {
      const productNatureOptionsFromProductNatureIds =
        await buildProductNatureOptionsFromProductNatureIds(
          config.productNatureIds,
        );
      setProductNaturesOptions(productNatureOptionsFromProductNatureIds);
    }
    if (
      config.retailFamilyIds &&
      config.offerDate &&
      config.retailFamilyIds.length > 0
    ) {
      const retailFamilyOptionsFromRetailFamilyIds =
        await buildRetailFamilyOptionsFromRetailFamilyIds(
          config.retailFamilyIds,
          config.offerDate,
        );
      setRetailFamiliesOptions(retailFamilyOptionsFromRetailFamilyIds);
    }
    const sportsOptionsFromSportsIds = await buildSportsOptionsFromSportsIds(
      config.sportIds || [],
    );
    setSportsOptions(sportsOptionsFromSportsIds);
    setDateSelected([
      config.offerDate ? new Date(config.offerDate) : new Date(1970, 0),
    ]);
    setConfigBuilded(true);
  };

  useEffect(() => {
    if (userConfiguration) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      buildConfiguration(userConfiguration);
    }
  }, [userConfiguration]);

  const onSave = () => {
    apiUsers
      .setConfiguration({
        sportIds: sportsIds,
        offerDate: format(dateSelected[0], 'yyyy-MM-dd'),
        productNatureIds: productNaturesOptions.map((pn) => pn.value),
        retailFamilyIds: retailFamiliesOptions.map((rf) => rf.value),
      })
      .then(() => {
        storageHelper.removeItemsFromSession('personal-offer_my-view');
        navigate('/personal-offer/my-view');
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const onChangeDate = (value: Date[]) => {
    setDateSelected(value);
    setRetailFamiliesOptions([]);
  };

  const buildSteps = () => {
    const welcomeStep = [
      {
        label: t('stepper.welcome'),
        content: <Welcome user={currentUser} />,
        hasNextButton: true,
        hasPreviousButton: false,
      },
    ];

    const otherSteps = [
      {
        label: t('stepper.conf_your_param'),
        contentTitle: t('stepper.conf_your_param'),
        content: (
          <SportsSearchInput
            onChange={setSportsOptions}
            selectedSportsOptions={sportsOptions}
          />
        ),
        hasNextButton: true,
        hasPreviousButton: true,
        canGoNext: doesUserHaveSports,
      },
      {
        label: t('stepper.specify_view'),
        contentTitle: t('stepper.specify_view'),
        content: (
          <>
            <InputCalendar
              minSelectableDate={[tomorrowDate]}
              maxSelectableDate={[todayDateAdd20years]}
              selectedDate={dateSelected}
              onChange={onChangeDate}
              labelInput={t('calendar.label_input')}
              displaySubline
            />
            <>
              <VpDivider className="divider" />
              <ProductNaturesSearchInput
                onChange={setProductNaturesOptions}
                selectedProductNatureOptions={productNaturesOptions}
                sportIds={sportsIds}
              />
              <RetailFamiliesSearchInput
                onChange={setRetailFamiliesOptions}
                date={retailFamiliesDate}
                selectedRetailFamilyOptions={retailFamiliesOptions}
                disabled={!didUserSelectedADate}
              />
            </>
          </>
        ),
        hasNextButton: false,
        hasPreviousButton: true,
      },
    ];

    return userHasConfig ? otherSteps : [...welcomeStep, ...otherSteps];
  };

  const calculateCurrentStep = () => {
    if (location.search.includes('step')) {
      return parseInt(location.search.split('=')[1]);
    } else return 0;
  };

  return (
    <Layout>
      {hasRoleVisualization ? (
        <>
          <Header
            logoTitle={t('header.config_title')}
            env={!environment.production ? environment.env : undefined}
            darkSkin
          />
          <div data-testid="my-config" className="my-config">
            {shouldLoadStepper && (
              <Stepper
                saveButtonLabel={t('stepper.save')}
                steps={buildSteps()}
                save={onSave}
                canSave={canSave}
                currentStep={calculateCurrentStep()}
              />
            )}
          </div>
        </>
      ) : (
        <ErrorPage
          errorPageTitle={t('no-access-page.title')}
          errorMessage={t('no-access-page.subtitle')}
        />
      )}
    </Layout>
  );
};
